if (!customElements.get('dy-ugc-video-lookbook')) {
  class DyUgcVideoLookbook extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId || '';
      this.autoplayEnabled = this.dataset.autoplay !== 'false';
      this.sliderEl = this.querySelector('[data-dy-uvl-slider]');
      this.track = this.querySelector('.swiper-wrapper');
      this.modal = this.querySelector('[data-dy-uvl-modal]');
      this.player = this.querySelector('[data-dy-uvl-player]');
      this.modalSoundBtn = this.querySelector(
        '[data-dy-uvl-modal] [data-dy-uvl-sound]'
      );

      if (!this.sliderEl || !this.track) return;

      this.swiper = null;
      this.initAttempts = 0;
      this.expandedCard = null;
      this.playingCard = null;
      this.lastFocused = null;
      this.expandedFromInline = false;
      this.videoObserver = null;
      this.visibilityObserver = null;
      this.userPausedCards = new WeakSet();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.marqueeEnabled = false;
      this.marqueeSecondsDesktop = 6;
      this.marqueeSecondsMobile = 8;
      this.marqueeSecondsPerCard = 6;
      this.interactionPauseMs = 2500;
      this.marqueeBlockers = new Set();
      this.marqueeRaf = null;
      this.marqueeRetryTimer = null;
      this.marqueeTouchResumeTimer = null;
      this.marqueeLastTs = 0;
      this.marqueeStride = 0;
      this.marqueeResumeAfterTransition = false;
      this.ignoreHoverUntil = 0;
      this.onMarqueeFrame = this.handleMarqueeFrame.bind(this);
      this.onMarqueeBreakpoint = this.handleMarqueeBreakpoint.bind(this);

      this.onSliderClick = this.handleSliderClick.bind(this);
      this.onPlayToggleClick = this.handlePlayToggleClick.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onSectionUnload = this.handleSectionUnload.bind(this);
      this.onModalSoundClick = this.handleModalSoundClick.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);

      this.sliderEl.addEventListener('click', this.onSliderClick);
      // Capture-phase so play/pause always wins over delegated slider click quirks.
      this.sliderEl.addEventListener('click', this.onPlayToggleClick, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.querySelectorAll('[data-dy-uvl-close]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.closeModal());
      });

      if (this.modalSoundBtn) {
        this.modalSoundBtn.addEventListener('click', this.onModalSoundClick);
      }

      this.initSwiper();
    }

    disconnectedCallback() {
      this.destroySwiper();
      this.stopVideoObserver();
      this.unbindVisibilityObserver();

      this.sliderEl?.removeEventListener('click', this.onSliderClick);
      this.sliderEl?.removeEventListener('click', this.onPlayToggleClick, true);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.modalSoundBtn?.removeEventListener('click', this.onModalSoundClick);

      this._initialized = false;
      document.body.style.overflow = '';
      this.stopInlinePlayback();
      this.clearModalPlayer();
    }

    /* ------------------------------------------------------------------ */
    /* Swiper infinite slider                                             */
    /* ------------------------------------------------------------------ */

    getSwiperConstructor() {
      if (window.MinimogLibs && typeof window.MinimogLibs.Swiper === 'function') {
        return window.MinimogLibs.Swiper;
      }
      return null;
    }

    readCssNumber(property, fallback) {
      const value = parseFloat(
        window.getComputedStyle(this).getPropertyValue(property)
      );
      return Number.isNaN(value) ? fallback : value;
    }

    getSlideSpeed() {
      const speed = parseInt(this.sliderEl.dataset.slideSpeed, 10);
      if (Number.isNaN(speed) || speed < 300) return 650;
      if (speed > 1200) return 1200;
      return speed;
    }

    getAutoplayConfig() {
      const enabled =
        this.sliderEl.dataset.autorotate === 'true' && !this.reducedMotion;

      const clampSeconds = (raw, fallback) => {
        let seconds = parseFloat(raw);
        if (Number.isNaN(seconds) || seconds < 2) seconds = fallback;
        if (seconds > 15) seconds = 15;
        return seconds;
      };

      let pauseSeconds = parseFloat(this.sliderEl.dataset.interactionPause);
      if (Number.isNaN(pauseSeconds) || pauseSeconds < 1) pauseSeconds = 2.5;
      if (pauseSeconds > 5) pauseSeconds = 5;

      return {
        enabled,
        secondsDesktop: clampSeconds(this.sliderEl.dataset.autorotateSpeed, 6),
        secondsMobile: clampSeconds(
          this.sliderEl.dataset.autorotateSpeedMobile,
          8
        ),
        interactionPauseMs: Math.round(pauseSeconds * 1000),
      };
    }

    isMobileViewport() {
      try {
        return window.matchMedia('(max-width: 767px)').matches;
      } catch (e) {
        return false;
      }
    }

    syncMarqueeSpeedFromViewport() {
      this.marqueeSecondsPerCard = this.isMobileViewport()
        ? this.marqueeSecondsMobile
        : this.marqueeSecondsDesktop;
    }

    getOffsets() {
      const edgeOffset = this.readCssNumber('--dy-uvl-edge-offset', 0);
      let offsetAfter = edgeOffset;

      if (window.matchMedia('(max-width: 767px)').matches) {
        const cardWidth = this.readCssNumber('--dy-uvl-card-width', 200);
        const viewportWidth = this.sliderEl.clientWidth || 0;
        offsetAfter = Math.max(edgeOffset, viewportWidth - cardWidth - edgeOffset);
      }

      return { before: edgeOffset, after: offsetAfter };
    }

    getOriginalCards() {
      return Array.from(
        this.track.querySelectorAll(
          '.swiper-slide[data-dy-uvl-slide]:not(.swiper-slide-duplicate)'
        )
      );
    }

    getAllVideoCards() {
      // Exclude Swiper's loop clones (.swiper-slide-duplicate). Swiper's loop
      // engine physically repositions/recycles those DOM nodes as the user
      // swipes near the boundaries, and browsers commonly pause/reset a
      // <video> element when its node is moved — that's what shows up as the
      // video going black for ~1s then coming back. Original slides are not
      // the ones Swiper relocates, so restricting playback to them avoids it.
      return Array.from(
        this.sliderEl.querySelectorAll('[data-dy-uvl-card]:not(.swiper-slide-duplicate)')
      );
    }

    initSwiper() {
      const SwiperCtor = this.getSwiperConstructor();
      if (!SwiperCtor) {
        this.initAttempts += 1;
        if (this.initAttempts <= 25) {
          window.setTimeout(() => this.initSwiper(), 120);
        }
        return;
      }

      this.destroySwiper();

      const offsets = this.getOffsets();
      const autoplayConfig = this.getAutoplayConfig();
      // slide_speed only drives manual swipe snapping now; continuous scroll is
      // frame-driven and uses autorotate_speed instead.
      const slideSpeed = this.reducedMotion ? 0 : this.getSlideSpeed();
      const slideCount = this.track.querySelectorAll(
        '.swiper-slide[data-dy-uvl-slide]:not(.swiper-slide-duplicate)'
      ).length;

      if (slideCount < 1) return;

      // True infinite loop when there are enough cards. Below that there is not
      // enough duplicated runway to wrap seamlessly, so the slider stays manual.
      const enableLoop = slideCount >= 3;

      const gap = this.readCssNumber('--dy-uvl-gap', 16);

      // Continuous scroll is frame-driven (rAF), not Swiper autoplay — so pause
      // is instant and motion stays linear on every device.
      this.marqueeEnabled = autoplayConfig.enabled && enableLoop;
      this.marqueeSecondsDesktop = autoplayConfig.secondsDesktop;
      this.marqueeSecondsMobile = autoplayConfig.secondsMobile;
      this.interactionPauseMs = autoplayConfig.interactionPauseMs;
      this.syncMarqueeSpeedFromViewport();
      this.marqueeBlockers.clear();
      this.marqueeResumeAfterTransition = false;
      this.ignoreHoverUntil = 0;
      this.marqueeStride = 0;
      this.clearTouchResumeTimer();

      this.swiper = new SwiperCtor(this.sliderEl, {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        // Must match CSS margin-right so translate math and visible gaps stay aligned.
        spaceBetween: gap,
        slidesOffsetBefore: offsets.before,
        slidesOffsetAfter: enableLoop ? gap : offsets.after,
        speed: slideSpeed,
        threshold: 4,
        grabCursor: true,
        watchOverflow: true,
        resistanceRatio: 0.65,
        longSwipesRatio: 0.35,
        longSwipesMs: 300,
        followFinger: true,
        slideToClickedSlide: false,
        // Pixel-rounding helps manual drag on Android, but fights continuous
        // sub-pixel marquee motion — keep it off while the marquee runs.
        roundLengths: !this.marqueeEnabled,
        observer: false,
        observeParents: false,
        loop: enableLoop,
        loopedSlides: enableLoop ? slideCount : undefined,
        loopAdditionalSlides: enableLoop ? 2 : 0,
        autoplay: false,
        on: {
          touchStart: () => {
            // Pause instantly. Do NOT set is-dragging here — that disables
            // pointer-events on cards and kills play/shop/fullscreen taps.
            this.ignoreHoverUntil = Date.now() + 900;
            this.marqueeResumeAfterTransition = false;
            this.clearTouchResumeTimer();
            this.blockMarquee('touch');
          },
          sliderMove: () => {
            this.beginDrag();
            this.clearTouchResumeTimer();
            this.blockMarquee('touch');
          },
          touchEnd: () => {
            this.ignoreHoverUntil = Date.now() + 900;
            // Settle a frame so Swiper can mark allowClick / animating correctly.
            window.setTimeout(() => this.releaseTouchMarquee(), 40);
          },
          transitionEnd: () => {
            this.endDrag();
            this.ensureSlideGaps();
            if (this.autoplayEnabled) this.syncVisibleVideos();
            if (this.marqueeResumeAfterTransition) {
              this.marqueeResumeAfterTransition = false;
              // After a swipe snap, still give the user time to tap a card.
              this.scheduleTouchResume();
            }
          },
          init: () => {
            this.ensureSlideGaps();
            this.cacheMarqueeStride();
            if (this.autoplayEnabled) this.startAutoplayVideos();
          },
          resize: () => {
            if (!this.swiper) return;
            const nextOffsets = this.getOffsets();
            const nextGap = this.readCssNumber('--dy-uvl-gap', 16);
            this.swiper.params.slidesOffsetBefore = nextOffsets.before;
            this.swiper.params.slidesOffsetAfter = enableLoop ? nextGap : nextOffsets.after;
            this.swiper.params.spaceBetween = nextGap;
            if (typeof this.swiper.update === 'function') this.swiper.update();
            this.ensureSlideGaps();
            this.cacheMarqueeStride();
            this.syncMarqueeSpeedFromViewport();
          },
        },
      });

      // Wired after construction: `this.swiper` is unset during Swiper's init cb.
      if (this.marqueeEnabled) {
        this.cacheMarqueeStride();
        this.bindVisibilityAutoplay();
        this.bindMarqueeHoverPause();
        this.bindMarqueeBreakpoint();
        this.startMarqueeLoop();
      }
    }

    releaseTouchMarquee() {
      if (!this.swiper || this.swiper.destroyed) {
        this.endDrag();
        this.scheduleTouchResume();
        return;
      }

      if (this.swiper.animating) {
        // Keep paused until the snap finishes — then transitionEnd schedules resume.
        this.marqueeResumeAfterTransition = true;
        return;
      }

      this.endDrag();
      if (this.autoplayEnabled) this.syncVisibleVideos();
      // Keep marquee paused long enough for taps (Shop / Play) on mobile.
      this.scheduleTouchResume();
    }

    clearTouchResumeTimer() {
      if (!this.marqueeTouchResumeTimer) return;
      window.clearTimeout(this.marqueeTouchResumeTimer);
      this.marqueeTouchResumeTimer = null;
    }

    scheduleTouchResume() {
      this.clearTouchResumeTimer();
      // Stay blocked under 'touch' until the pause window ends.
      this.marqueeBlockers.add('touch');
      this.stopMarqueeLoop();
      const delay = this.interactionPauseMs || 2500;
      this.marqueeTouchResumeTimer = window.setTimeout(() => {
        this.marqueeTouchResumeTimer = null;
        this.unblockMarquee('touch');
      }, delay);
    }

    handleMarqueeBreakpoint() {
      this.syncMarqueeSpeedFromViewport();
      this.cacheMarqueeStride();
    }

    bindMarqueeBreakpoint() {
      this.unbindMarqueeBreakpoint();
      try {
        this._marqueeMq = window.matchMedia('(max-width: 767px)');
        if (this._marqueeMq.addEventListener) {
          this._marqueeMq.addEventListener('change', this.onMarqueeBreakpoint);
        } else if (this._marqueeMq.addListener) {
          this._marqueeMq.addListener(this.onMarqueeBreakpoint);
        }
      } catch (e) {
        this._marqueeMq = null;
      }
    }

    unbindMarqueeBreakpoint() {
      if (!this._marqueeMq) return;
      try {
        if (this._marqueeMq.removeEventListener) {
          this._marqueeMq.removeEventListener('change', this.onMarqueeBreakpoint);
        } else if (this._marqueeMq.removeListener) {
          this._marqueeMq.removeListener(this.onMarqueeBreakpoint);
        }
      } catch (e) {
        /* noop */
      }
      this._marqueeMq = null;
    }

    ensureSlideGaps() {
      if (!this.sliderEl) return;
      const gap = this.readCssNumber('--dy-uvl-gap', 16);
      if (this.swiper?.params) {
        this.swiper.params.spaceBetween = gap;
      }
      this.sliderEl.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.style.marginRight = `${gap}px`;
      });
    }

    beginDrag() {
      if (!this.sliderEl) return;
      this.sliderEl.classList.add('is-dragging');
    }

    endDrag() {
      if (!this.sliderEl) return;
      this.sliderEl.classList.remove('is-dragging');
    }

    isDragging() {
      return Boolean(this.sliderEl?.classList.contains('is-dragging'));
    }

    destroySwiper() {
      this.marqueeEnabled = false;
      this.clearTouchResumeTimer();
      this.stopMarqueeLoop();
      this.marqueeBlockers.clear();
      this.unbindVisibilityAutoplay();
      this.unbindMarqueeHoverPause();
      this.unbindMarqueeBreakpoint();

      if (this.sliderEl) {
        this.sliderEl.classList.remove('is-dragging');
        this.sliderEl.classList.remove('is-marquee');
      }

      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (error) {
          /* noop */
        }
        this.swiper = null;
      }
    }

    /* ------------------------------------------------------------------ */
    /* Continuous auto-scroll (frame driven, instantly pausable)          */
    /* ------------------------------------------------------------------ */

    cacheMarqueeStride() {
      const slide = this.track?.querySelector('.swiper-slide');
      if (!slide) {
        this.marqueeStride = 0;
        return 0;
      }
      const width = slide.getBoundingClientRect().width;
      if (!width) {
        this.marqueeStride = 0;
        return 0;
      }
      this.marqueeStride = width + this.readCssNumber('--dy-uvl-gap', 16);
      return this.marqueeStride;
    }

    getMarqueeSpeed() {
      const stride = this.marqueeStride || this.cacheMarqueeStride();
      if (!stride) return 0;
      const seconds = this.marqueeSecondsPerCard || 4;
      return stride / (seconds * 1000);
    }

    blockMarquee(reason) {
      this.marqueeBlockers.add(reason);
      this.stopMarqueeLoop();
    }

    unblockMarquee(reason) {
      this.marqueeBlockers.delete(reason);
      this.startMarqueeLoop();
    }

    stopMarqueeLoop() {
      if (this.marqueeRaf) {
        window.cancelAnimationFrame(this.marqueeRaf);
        this.marqueeRaf = null;
      }
      if (this.marqueeRetryTimer) {
        window.clearTimeout(this.marqueeRetryTimer);
        this.marqueeRetryTimer = null;
      }
      this.marqueeLastTs = 0;
      this.sliderEl?.classList.remove('is-marquee');
    }

    startMarqueeLoop() {
      if (!this.marqueeEnabled || !this.swiper || this.swiper.destroyed) return;
      if (this.marqueeBlockers.size || this.marqueeRaf) return;
      if (this.modal && !this.modal.hidden) return;

      // Never fight a snap/momentum animation — let it settle, then take over.
      if (this.swiper.animating || this.isDragging()) {
        if (this.marqueeRetryTimer) return;
        this.marqueeRetryTimer = window.setTimeout(() => {
          this.marqueeRetryTimer = null;
          this.startMarqueeLoop();
        }, 100);
        return;
      }

      if (!this.marqueeStride) this.cacheMarqueeStride();
      this.marqueeLastTs = 0;
      this.swiper.setTransition(0);
      this.sliderEl?.classList.add('is-marquee');
      this.marqueeRaf = window.requestAnimationFrame(this.onMarqueeFrame);
    }

    handleMarqueeFrame(timestamp) {
      this.marqueeRaf = null;

      const swiper = this.swiper;
      if (!this.marqueeEnabled || !swiper || swiper.destroyed) return;
      if (this.marqueeBlockers.size) return;

      if (!this.marqueeLastTs) {
        this.marqueeLastTs = timestamp;
      } else {
        // Cap the delta so a dropped / backgrounded tab cannot jump the track.
        const delta = Math.min(timestamp - this.marqueeLastTs, 48);
        this.marqueeLastTs = timestamp;

        const pxPerMs = this.getMarqueeSpeed();
        if (pxPerMs > 0) {
          const previousIndex = swiper.activeIndex;
          // Prefer getTranslate when available — stays accurate after user drag.
          const current =
            typeof swiper.getTranslate === 'function'
              ? swiper.getTranslate()
              : swiper.translate;
          swiper.setTranslate(current - pxPerMs * delta);

          // loopFix reads layout — only when a card boundary is crossed.
          if (typeof swiper.updateActiveIndex === 'function') swiper.updateActiveIndex();
          if (
            swiper.params.loop &&
            swiper.activeIndex !== previousIndex &&
            typeof swiper.loopFix === 'function'
          ) {
            swiper.loopFix();
            // loopFix can re-enable CSS transition; keep marquee transitionless.
            swiper.setTransition(0);
          }
        }
      }

      this.marqueeRaf = window.requestAnimationFrame(this.onMarqueeFrame);
    }

    canUseHoverPause() {
      try {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      } catch (e) {
        return false;
      }
    }

    bindMarqueeHoverPause() {
      this.unbindMarqueeHoverPause();
      if (!this.sliderEl || !this.canUseHoverPause()) return;

      // pointerenter/leave with mouse-only filter avoids Android's fake hover
      // after a tap (which has no matching leave and would freeze the track).
      this._onMarqueeEnter = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        if (Date.now() < this.ignoreHoverUntil) return;
        this.blockMarquee('hover');
      };
      this._onMarqueeLeave = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        this.unblockMarquee('hover');
      };

      if (typeof window.PointerEvent === 'function') {
        this.sliderEl.addEventListener('pointerenter', this._onMarqueeEnter);
        this.sliderEl.addEventListener('pointerleave', this._onMarqueeLeave);
        this._marqueeHoverUsesPointer = true;
      } else {
        this.sliderEl.addEventListener('mouseenter', this._onMarqueeEnter);
        this.sliderEl.addEventListener('mouseleave', this._onMarqueeLeave);
        this._marqueeHoverUsesPointer = false;
      }
    }

    unbindMarqueeHoverPause() {
      this.marqueeBlockers.delete('hover');
      if (!this.sliderEl) return;
      if (this._onMarqueeEnter) {
        if (this._marqueeHoverUsesPointer) {
          this.sliderEl.removeEventListener('pointerenter', this._onMarqueeEnter);
          this.sliderEl.removeEventListener('pointerleave', this._onMarqueeLeave);
        } else {
          this.sliderEl.removeEventListener('mouseenter', this._onMarqueeEnter);
          this.sliderEl.removeEventListener('mouseleave', this._onMarqueeLeave);
        }
        this._onMarqueeEnter = null;
        this._onMarqueeLeave = null;
      }
    }

    isSliderInView() {
      if (!this.sliderEl) return false;
      const rect = this.sliderEl.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.bottom > 0 && rect.top < viewportHeight;
    }

    bindVisibilityAutoplay() {
      this.unbindVisibilityAutoplay();

      const sync = () => {
        if (document.hidden) this.blockMarquee('hidden');
        else this.unblockMarquee('hidden');
      };

      document.addEventListener('visibilitychange', sync);
      this._visibilitySync = sync;

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.unblockMarquee('offscreen');
              else this.blockMarquee('offscreen');
            });
          },
          { threshold: 0.15 }
        );
        this.visibilityObserver.observe(this.sliderEl);
      } else if (!this.isSliderInView()) {
        this.blockMarquee('offscreen');
      }

      sync();
    }

    unbindVisibilityAutoplay() {
      if (this._visibilitySync) {
        document.removeEventListener('visibilitychange', this._visibilitySync);
        this._visibilitySync = null;
      }
      this.unbindVisibilityObserver();
    }

    unbindVisibilityObserver() {
      if (!this.visibilityObserver) return;
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.blockMarquee('hidden');
        this.getAllVideoCards().forEach((card) => {
          if (card !== this.expandedCard) this.pauseInlinePlayback(card);
        });
        return;
      }

      this.unblockMarquee('hidden');
      if (this.autoplayEnabled) this.startAutoplayVideos();
    }

    /* ------------------------------------------------------------------ */
    /* Click / keyboard                                                   */
    /* ------------------------------------------------------------------ */

    handlePlayToggleClick(event) {
      const playToggle = event.target.closest('[data-dy-uvl-play-toggle]');
      if (!playToggle || !this.sliderEl.contains(playToggle)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const card = playToggle.closest('[data-dy-uvl-card]');
      if (card) this.toggleInlinePlayback(card);
    }

    handleSliderClick(event) {
      // Play/pause is handled in capture via handlePlayToggleClick.
      if (event.target.closest('[data-dy-uvl-play-toggle]')) return;

      // Ignore only ghost clicks after a real swipe. Never gate on the
      // is-dragging class here — that class was set too early on tap and
      // preventedDefault on product links / blocked video controls.
      if (this.swiper && this.swiper.allowClick === false) {
        event.preventDefault();
        return;
      }

      // Let theme Product Quick View (or a plain product link, in redirect
      // mode) handle shop / product buttons untouched.
      // Do not stopPropagation — Minimog listens via document delegation.
      if (
        event.target.closest(
          '[data-dy-uvl-shop-link], .m-product-quickview-button, .dy-uvl__product-info, .dy-uvl__shop-btn'
        )
      ) {
        return;
      }

      const fullscreenBtn = event.target.closest('[data-dy-uvl-card-fullscreen]');
      if (fullscreenBtn) {
        event.preventDefault();
        event.stopPropagation();
        const card = fullscreenBtn.closest('[data-dy-uvl-card]');
        if (card) this.openModal(card);
        return;
      }

      // Tapping anywhere else on a video card (poster, gradient overlay,
      // empty space around the center play button) toggles playback too,
      // not just the small play button itself.
      const card = event.target.closest('[data-dy-uvl-card]');
      if (card) {
        this.toggleInlinePlayback(card);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Video playback                                                     */
    /* ------------------------------------------------------------------ */

    toAbsoluteUrl(value) {
      if (!value) return '';
      try {
        return new URL(value, window.location.href).href;
      } catch (e) {
        return value;
      }
    }

    getVideoConfig(card) {
      const safeString = (value) => {
        try {
          return (value ?? '').toString().trim();
        } catch (e) {
          return '';
        }
      };

      return {
        type: safeString(card.getAttribute('data-video-type')),
        id: safeString(card.getAttribute('data-video-id')),
        src: safeString(card.getAttribute('data-video-src')),
        useShopifyTemplate: card.hasAttribute('data-video-shopify'),
      };
    }

    getInlinePlayer(card) {
      return card?.querySelector('[data-dy-uvl-inline-player]') || null;
    }

    showCardFullscreenButton(card) {
      const btn = card?.querySelector('[data-dy-uvl-card-fullscreen]');
      if (!btn) return;
      btn.hidden = false;
      btn.classList.add('is-visible');
    }

    prepareHtml5Video(video, { controls = false } = {}) {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = this.autoplayEnabled;
      video.loop = true;
      video.controls = controls;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      if (this.autoplayEnabled) video.setAttribute('autoplay', '');
      else video.removeAttribute('autoplay');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'metadata';
    }

    tryPlayVideo(video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    absoluteizeMediaSources(video) {
      video.querySelectorAll('source').forEach((source) => {
        const raw = source.getAttribute('src');
        if (!raw) return;
        source.setAttribute('src', this.toAbsoluteUrl(raw));
      });

      const rawSrc = video.getAttribute('src');
      if (rawSrc) video.setAttribute('src', this.toAbsoluteUrl(rawSrc));
    }

    mountShopifyVideo(card, container, { controls = false } = {}) {
      const template = card.querySelector('[data-dy-uvl-video-template]');
      if (!template || !container) return null;

      const fragment = template.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!video) return null;

      if (video.parentNode) video.parentNode.removeChild(video);
      this.absoluteizeMediaSources(video);
      this.prepareHtml5Video(video, { controls });
      container.appendChild(video);
      video.load();
      return video;
    }

    mountMp4Video(src, container, { controls = false } = {}) {
      if (!src || !container) return null;
      const video = document.createElement('video');
      video.src = this.toAbsoluteUrl(src);
      this.prepareHtml5Video(video, { controls });
      container.appendChild(video);
      video.load();
      return video;
    }

    mountYoutube(id, container) {
      if (!id || !container) return null;
      const yt = document.createElement('iframe');
      yt.src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${encodeURIComponent(id)}&rel=0&playsinline=1&enablejsapi=1`;
      yt.title = 'UGC video';
      yt.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      yt.allowFullscreen = true;
      yt.setAttribute('allowfullscreen', '');
      container.appendChild(yt);
      return yt;
    }

    mountVimeo(id, container) {
      if (!id || !container) return null;
      const vimeo = document.createElement('iframe');
      vimeo.src = `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&playsinline=1`;
      vimeo.title = 'UGC video';
      vimeo.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      vimeo.allowFullscreen = true;
      vimeo.setAttribute('allowfullscreen', '');
      container.appendChild(vimeo);
      return vimeo;
    }

    buildMedia(config, card, container, { controls = false } = {}) {
      if (!container) return null;
      container.innerHTML = '';

      if ((config.type === 'youtube' || config.type === 'vimeo') && config.id) {
        return config.type === 'youtube'
          ? this.mountYoutube(config.id, container)
          : this.mountVimeo(config.id, container);
      }

      if (config.src) {
        return this.mountMp4Video(config.src, container, { controls });
      }

      if (config.useShopifyTemplate || config.type === 'shopify') {
        return this.mountShopifyVideo(card, container, { controls });
      }

      return null;
    }

    bindPlaybackUi(media, { onStart } = {}) {
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        const start = () => onStart?.();

        // Autoplay attribute can start playback before listeners attach —
        // if already playing, sync UI immediately instead of waiting for
        // another 'play' event that will never come.
        if (!media.paused && !media.ended) {
          start();
          return;
        }

        media.addEventListener('play', start, { once: true });
        media.addEventListener('playing', start, { once: true });
        media.addEventListener('loadedmetadata', () => this.tryPlayVideo(media), {
          once: true,
        });
        media.addEventListener('canplay', () => this.tryPlayVideo(media), {
          once: true,
        });
        // A real load failure (bad URL, unsupported codec) must not leave the
        // poster hidden behind a dead <video>: drop it so the poster stays
        // visible and a later playInline() retry rebuilds a fresh element.
        media.addEventListener(
          'error',
          () => {
            const container = media.parentNode;
            const card = container?.closest('[data-dy-uvl-card]');
            if (container) container.innerHTML = '';
            if (card) this.setCardPlayingState(card, false);
          },
          { once: true }
        );
        this.tryPlayVideo(media);
        return;
      }

      // YouTube/Vimeo iframe: treat as playing once mounted (autoplay=1 in src).
      onStart?.();
    }

    startAutoplayVideos() {
      this.stopVideoObserver();
      const videoCards = this.getAllVideoCards();
      if (!videoCards.length) return;

      if (!('IntersectionObserver' in window)) {
        videoCards.forEach((card) => this.playInline(card));
        return;
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          if (this.isDragging()) return;

          entries.forEach((entry) => {
            const card = entry.target;
            if (card === this.expandedCard) return;
            if (this.userPausedCards.has(card)) return;

            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.2 &&
              !document.hidden
            ) {
              this.playInline(card);
            } else {
              this.pauseInlinePlayback(card, { fromAutoplay: true });
            }
          });
        },
        {
          root: this.sliderEl,
          rootMargin: '0px 80px',
          threshold: [0, 0.2],
        }
      );

      videoCards.forEach((card) => this.videoObserver.observe(card));
    }

    syncVisibleVideos() {
      if (!this.autoplayEnabled || !this.swiper || this.isDragging()) return;

      this.getAllVideoCards().forEach((card) => {
        if (card === this.expandedCard) return;
        if (this.userPausedCards.has(card)) return;
        const rect = card.getBoundingClientRect();
        const rootRect = this.sliderEl.getBoundingClientRect();
        const visible =
          rect.right > rootRect.left + 8 && rect.left < rootRect.right - 8;

        if (visible) this.playInline(card);
        else this.pauseInlinePlayback(card, { fromAutoplay: true });
      });
    }

    stopVideoObserver() {
      if (!this.videoObserver) return;
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    setCardPlayingState(card, isPlaying) {
      if (!card) return;
      card.classList.toggle('is-playing', isPlaying);
      const toggle = card.querySelector('[data-dy-uvl-play-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        toggle.setAttribute('aria-pressed', String(isPlaying));
      }
    }

    isCardActivelyPlaying(card) {
      if (!card) return false;
      if (card.classList.contains('is-playing')) return true;

      const media = this.getInlinePlayer(card)?.querySelector('video, iframe') || null;
      if (!media) return false;
      if (media.tagName === 'VIDEO') return !media.paused && !media.ended;
      // iframe present means embed is (or was) playing
      return true;
    }

    toggleInlinePlayback(card) {
      if (!card) return;

      if (this.isCardActivelyPlaying(card)) {
        this.userPausedCards.add(card);
        this.pauseInlinePlayback(card);
        return;
      }

      this.userPausedCards.delete(card);
      this.playInline(card, { userInitiated: true });
    }

    pauseInlinePlayback(card, { fromAutoplay = false } = {}) {
      if (!card) return;

      const container = this.getInlinePlayer(card);
      const media = container?.querySelector('video, iframe') || null;

      if (media?.tagName === 'VIDEO') {
        media.pause();
      } else if (container) {
        container.innerHTML = '';
      }

      this.setCardPlayingState(card, false);
      this.showCardFullscreenButton(card);

      if (!fromAutoplay && this.playingCard === card) {
        this.playingCard = null;
      }
    }

    playInline(card, { userInitiated = false } = {}) {
      if (!card) return;
      if (!userInitiated && this.isDragging()) return;
      if (!userInitiated && this.userPausedCards.has(card)) return;

      const container = this.getInlinePlayer(card);
      if (!container) return;

      const existingMedia = container.querySelector('video, iframe');
      if (existingMedia) {
        this.showCardFullscreenButton(card);
        if (existingMedia.tagName === 'VIDEO') {
          existingMedia.muted = true;

          // Already playing — just sync the pause icon / is-playing state.
          if (!existingMedia.paused && !existingMedia.ended) {
            this.setCardPlayingState(card, true);
            return;
          }

          // Wait for a confirmed 'play'/'playing' before revealing the
          // player — otherwise a blocked/failed play() leaves the poster
          // hidden behind an empty black box instead of staying visible.
          this.bindPlaybackUi(existingMedia, {
            onStart: () => this.setCardPlayingState(card, true),
          });
        } else {
          this.setCardPlayingState(card, true);
        }
        return;
      }

      const media = this.buildMedia(this.getVideoConfig(card), card, container, {
        controls: false,
      });
      if (!media) return;

      this.showCardFullscreenButton(card);
      this.bindPlaybackUi(media, {
        onStart: () => {
          this.setCardPlayingState(card, true);
          this.showCardFullscreenButton(card);
        },
      });
    }

    stopInlinePlayback(targetCard = null) {
      const cards = targetCard ? [targetCard] : this.getAllVideoCards();

      cards.forEach((card) => {
        const container = this.getInlinePlayer(card);
        if (container) {
          const video = container.querySelector('video');
          if (video) video.pause();
          container.innerHTML = '';
        }
        this.setCardPlayingState(card, false);
        this.showCardFullscreenButton(card);
        this.userPausedCards.delete(card);
      });

      if (!targetCard || this.playingCard === targetCard) this.playingCard = null;
    }

    /* ------------------------------------------------------------------ */
    /* Modal popup only                                                   */
    /* ------------------------------------------------------------------ */

    clearModalPlayer() {
      if (!this.player) return;
      const media = this.player.querySelector('video, iframe');
      if (media && media.tagName === 'VIDEO') media.pause();
      this.player.innerHTML = '';
      if (this.modalSoundBtn) this.modalSoundBtn.hidden = true;
    }

    setMediaMuted(media, muted) {
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        media.muted = muted;
        media.defaultMuted = muted;
        if (muted) media.setAttribute('muted', '');
        else media.removeAttribute('muted');
        if (!muted) this.tryPlayVideo(media);
        return;
      }

      const src = media.getAttribute('src') || '';
      if (src.includes('youtube.com')) {
        media.contentWindow?.postMessage(
          JSON.stringify({
            event: 'command',
            func: muted ? 'mute' : 'unMute',
            args: [],
          }),
          '*'
        );
      } else if (src.includes('vimeo.com')) {
        media.contentWindow?.postMessage(
          { method: 'setVolume', value: muted ? 0 : 1 },
          '*'
        );
      }
    }

    updateSoundButton(muted) {
      if (!this.modalSoundBtn) return;
      this.modalSoundBtn.hidden = false;
      this.modalSoundBtn.classList.toggle('is-muted', muted);
      this.modalSoundBtn.setAttribute('aria-pressed', String(!muted));
      this.modalSoundBtn.setAttribute(
        'aria-label',
        muted ? 'Turn sound on' : 'Turn sound off'
      );
    }

    openModal(card) {
      if (!this.modal || !this.player || !card) return;

      this.blockMarquee('modal');
      this.lastFocused = document.activeElement;
      this.expandedFromInline = true;
      this.expandedCard = card;

      const inlinePlayer = this.getInlinePlayer(card);
      const inlineMedia = inlinePlayer?.querySelector('video, iframe') || null;
      this.player.innerHTML = '';

      let media = null;
      if (inlineMedia) {
        this.player.appendChild(inlineMedia);
        media = inlineMedia;
        if (media.tagName === 'VIDEO') {
          media.controls = true;
          this.tryPlayVideo(media);
        }
      } else {
        media = this.buildMedia(this.getVideoConfig(card), card, this.player, {
          controls: true,
        });
      }

      this.modal.hidden = false;
      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      this.setCardPlayingState(card, true);
      this.playingCard = card;
      this.setMediaMuted(media, false);
      this.updateSoundButton(false);

      if (!inlineMedia) this.bindPlaybackUi(media);

      const closeBtn = this.modal.querySelector('.dy-uvl__modal-close');
      if (closeBtn) closeBtn.focus();
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.hidden = true;
      document.body.style.overflow = '';

      const card = this.playingCard;
      const modalMedia = this.player?.querySelector('video, iframe') || null;
      const inlinePlayer = card ? this.getInlinePlayer(card) : null;

      if (this.expandedFromInline && card && inlinePlayer && modalMedia) {
        this.setMediaMuted(modalMedia, true);
        if (modalMedia.tagName === 'VIDEO') modalMedia.controls = false;
        inlinePlayer.appendChild(modalMedia);
        this.player.innerHTML = '';
        if (modalMedia.tagName === 'VIDEO') this.tryPlayVideo(modalMedia);
        this.showCardFullscreenButton(card);
      } else {
        this.clearModalPlayer();
        if (card) this.playInline(card);
      }

      this.expandedFromInline = false;
      this.expandedCard = null;
      this.playingCard = null;
      if (this.modalSoundBtn) this.modalSoundBtn.hidden = true;

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }

      this.unblockMarquee('modal');
    }

    handleModalSoundClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const media = this.player?.querySelector('video, iframe');
      if (!media) return;
      const shouldMute = !this.modalSoundBtn?.classList.contains('is-muted');
      this.setMediaMuted(media, shouldMute);
      this.updateSoundButton(shouldMute);
    }

    handleDocumentKeydown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.modal.querySelectorAll(
          'button:not([disabled]):not([hidden]), a[href], video[controls], iframe'
        )
      ).filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleSectionUnload(event) {
      if (!this.sectionId || event.detail.sectionId !== this.sectionId) return;
      this.destroySwiper();
      this.stopVideoObserver();
      document.body.style.overflow = '';
      this.stopInlinePlayback();
      this.clearModalPlayer();
    }
  }

  customElements.define('dy-ugc-video-lookbook', DyUgcVideoLookbook);
}
